import axios, { AxiosError, AxiosResponse } from "axios";
import { all, call, put, takeLatest } from "@redux-saga/core/effects";

import { RootState } from "data-handler/rootReducer";

export const STOCK_INSPECT_REQUEST =
  "schoolconnect/stockInspect/STOCK_INSPECT_REQUEST";
export const STOCK_INSPECT_SUCCESS =
  "schoolconnect/stockInspect/STOCK_INSPECT_SUCCESS";
export const STOCK_INSPECT_FAILURE =
  "schoolconnect/stockInspect/STOCK_INSPECT_FAILURE";
export const STOCK_INSPECT_LINK_INCIDENT_REQUEST =
  "schoolconnect/stockInspect/STOCK_INSPECT_LINK_INCIDENT_REQUEST";
export const STOCK_INSPECT_LINK_INCIDENT_SUCCESS =
  "schoolconnect/stockInspect/STOCK_INSPECT_LINK_INCIDENT_SUCCESS";
export const STOCK_INSPECT_LINK_INCIDENT_FAILURE =
  "schoolconnect/stockInspect/STOCK_INSPECT_LINK_INCIDENT_FAILURE";
export const STOCK_INSPECT_LINK_INCIDENT_RESET =
  "schoolconnect/stockInspect/STOCK_INSPECT_LINK_INCIDENT_RESET";

export type StockInfo = {
  commodity_name: string | null;
  commodity_id: number | null;
  school: string | null;
  school_id: number | null;
  category: string | null;
  country: string | null;
  country_id: number | null;
  commodity_in_school_profile: boolean | null;
  school_profile_id: number | null;
};

export type StockInspectRawRecord = {
  id: number;
  occurred_at: string;
  quantity?: number | string | null;
  sign?: number | null;
  rolling_balance?: number | string | null;
  type?: string | null;
  affected_by?: Array<
    | number
    | string
    | {
        id?: number | string;
        qty_fulfilled?: string | number | null;
        [k: string]: unknown;
      }
  > | null;
  occurred_against?: Array<
    | number
    | string
    | {
        id?: number | string;
        qty_fulfilled?: string | number | null;
        [k: string]: unknown;
      }
  > | null;
  affected_by_consumption_id?: Array<number | string> | null;
  affected_by_incident_id?: Array<number | string> | null;
  occurred_against_delivery_id?: Array<number | string> | null;
  occurred_against_purchase_id?: Array<number | string> | null;
  balance?: number | string | null;
  is_adhoc?: boolean | null;
  is_shell?: boolean | null;
  classification?: string | null;
  [key: string]: unknown;
};

export type StockInspectBalanceGates = {
  column_balance?: number | string | null;
  sum_positive_balances?: number | string | null;
  net_movements?: number | string | null;
  last_rolling_balance?: number | string | null;
  last_event_id?: number | null;
  event_count?: number | string | null;
  verdict?: string | null;
  disagreements?: string[] | null;
  ledger_unanimous?: boolean | null;
};

export type StockInspectResponse = {
  stock_info: StockInfo;
  balance_gates?: StockInspectBalanceGates | null;
  recordset: StockInspectRawRecord[];
};

type StockInspectState = {
  fetching: boolean;
  error: AxiosError | Error | null;
  linkIncident: {
    submitting: boolean;
    success: boolean;
    error: unknown | null;
  };
  data: {
    newStoreById: Record<string, StockInspectResponse>;
  };
};

const initialState: StockInspectState = {
  fetching: false,
  error: null,
  linkIncident: {
    submitting: false,
    success: false,
    error: null,
  },
  data: {
    newStoreById: {},
  },
};

type StockInspectRequestAction = {
  type: typeof STOCK_INSPECT_REQUEST;
  stockId: string;
};

type StockInspectSuccessAction = {
  type: typeof STOCK_INSPECT_SUCCESS;
  stockId: string;
  newStore: StockInspectResponse;
};

type StockInspectFailureAction = {
  type: typeof STOCK_INSPECT_FAILURE;
  stockId: string;
  error: AxiosError | Error;
};

type StockInspectLinkIncidentRequestAction = {
  type: typeof STOCK_INSPECT_LINK_INCIDENT_REQUEST;
  stockId: string;
  incidentId: number;
  positiveEventId: number;
};

type StockInspectLinkIncidentSuccessAction = {
  type: typeof STOCK_INSPECT_LINK_INCIDENT_SUCCESS;
};

type StockInspectLinkIncidentFailureAction = {
  type: typeof STOCK_INSPECT_LINK_INCIDENT_FAILURE;
  error: unknown;
};

type StockInspectLinkIncidentResetAction = {
  type: typeof STOCK_INSPECT_LINK_INCIDENT_RESET;
};

type Action =
  | StockInspectRequestAction
  | StockInspectSuccessAction
  | StockInspectFailureAction
  | StockInspectLinkIncidentRequestAction
  | StockInspectLinkIncidentSuccessAction
  | StockInspectLinkIncidentFailureAction
  | StockInspectLinkIncidentResetAction;

const reducer = (
  state: StockInspectState = initialState,
  action: Action
): StockInspectState => {
  switch (action.type) {
    case STOCK_INSPECT_REQUEST: {
      const { stockId } = action as StockInspectRequestAction;
      const {
        [stockId]: _existingNewStore,
        ...otherNewStores
      } = state.data.newStoreById;
      return {
        ...state,
        fetching: true,
        error: null,
        linkIncident: {
          ...state.linkIncident,
          success: false,
        },
        data: {
          newStoreById: otherNewStores,
        },
      };
    }
    case STOCK_INSPECT_SUCCESS:
      return {
        ...state,
        fetching: false,
        error: null,
        data: {
          newStoreById: {
            ...state.data.newStoreById,
            [action.stockId]: action.newStore,
          },
        },
      };
    case STOCK_INSPECT_FAILURE:
      // Remove any stale data for the requested stock id when the call fails
      const {
        [action.stockId]: _newStore,
        ...remainingNewStore
      } = state.data.newStoreById;
      return {
        ...state,
        fetching: false,
        error: action.error,
        data: {
          newStoreById: remainingNewStore,
        },
      };
    case STOCK_INSPECT_LINK_INCIDENT_REQUEST:
      return {
        ...state,
        linkIncident: {
          submitting: true,
          success: false,
          error: null,
        },
      };
    case STOCK_INSPECT_LINK_INCIDENT_SUCCESS:
      return {
        ...state,
        linkIncident: {
          submitting: false,
          success: true,
          error: null,
        },
      };
    case STOCK_INSPECT_LINK_INCIDENT_FAILURE:
      return {
        ...state,
        linkIncident: {
          submitting: false,
          success: false,
          error: (action as StockInspectLinkIncidentFailureAction).error,
        },
      };
    case STOCK_INSPECT_LINK_INCIDENT_RESET:
      return {
        ...state,
        linkIncident: {
          submitting: false,
          success: false,
          error: null,
        },
      };
    default:
      return state;
  }
};

export default reducer;

const buildInspectUrl = (stockId: string) => {
  const trimmed = stockId.trim();
  const baseUrl = (process.env.REACT_APP_API_URL ?? "").replace(/\/$/, "");

  if (!trimmed) {
    throw new Error("Missing stock id for stock inspect request");
  }

  if (!baseUrl) {
    throw new Error("Missing API base URL for stock inspect");
  }

  return `${baseUrl}/store_manager/stock/inspect/${trimmed}/`;
};

const buildDedupUrl = (stockId: string, actionPath: string) => {
  const trimmed = stockId.trim();
  const baseUrl = (process.env.REACT_APP_API_URL ?? "").replace(/\/$/, "");
  if (!trimmed) throw new Error("Missing stock id for dedup request");
  if (!baseUrl) throw new Error("Missing API base URL for dedup");
  return `${baseUrl}/store_manager/stock/inspect/${trimmed}/${actionPath}/`;
};

export function previewDedup(stockId: string, excessEventId: number) {
  return axios
    .post(buildDedupUrl(stockId, "dedup-preview"), {
      excess_event_id: excessEventId,
    })
    .then((r) => r.data);
}

export function resolveDedup(
  stockId: string,
  keeperEventId: number,
  excessEventId: number
) {
  return axios
    .post(buildDedupUrl(stockId, "dedup-resolve"), {
      keeper_event_id: keeperEventId,
      excess_event_id: excessEventId,
    })
    .then((r) => r.data);
}

export function markKeepDedup(stockId: string, eventIds: number[]) {
  return axios
    .post(buildDedupUrl(stockId, "dedup-mark-keep"), { event_ids: eventIds })
    .then((r) => r.data);
}

export function reassignDedup(
  stockId: string,
  moves: { stock_event_id: number; to_store_event_id: number }[]
) {
  return axios
    .post(buildDedupUrl(stockId, "dedup-reassign"), { moves })
    .then((r) => r.data);
}

type ReconcilePayload = {
  moves?: { stock_event_id: number; to_store_event_id: number }[];
  eventIds?: number[];
  keepEventId?: number;
  mark?: boolean;
};

// one atomic call: apply moves, then keep-one/mark-all, heal once, roll back on failure
export function reconcileDedup(stockId: string, payload: ReconcilePayload) {
  return axios
    .post(buildDedupUrl(stockId, "dedup-reconcile"), {
      moves: payload.moves ?? [],
      event_ids: payload.eventIds ?? [],
      keep_event_id: payload.keepEventId,
      mark: payload.mark ?? false,
    })
    .then((r) => r.data);
}

function fetchStockInspect(stockId: string) {
  return axios.get<StockInspectResponse>(buildInspectUrl(stockId));
}

const buildLinkIncidentUrl = (stockId: string) => {
  const trimmed = stockId.trim();
  const baseUrl = (process.env.REACT_APP_API_URL ?? "").replace(/\/$/, "");

  if (!trimmed) {
    throw new Error("Missing stock id for link incident request");
  }

  if (!baseUrl) {
    throw new Error("Missing API base URL for stock inspect");
  }

  return `${baseUrl}/store_manager/store-doctor/${trimmed}/link-incident/`;
};

function linkIncident(
  stockId: string,
  incidentId: number,
  positiveEventId: number
) {
  return axios.post(
    buildLinkIncidentUrl(stockId),
    {
      incident_id: incidentId,
      positive_event_id: positiveEventId,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

export function* workerStockInspectSaga(action: StockInspectRequestAction) {
  const { stockId } = action;
  try {
    const response: AxiosResponse<StockInspectResponse> = yield call(
      fetchStockInspect,
      stockId
    );

    yield put({
      type: STOCK_INSPECT_SUCCESS,
      stockId,
      newStore: response.data,
    });
  } catch (error) {
    const parsedError = error as AxiosError | Error;
    yield put({
      type: STOCK_INSPECT_FAILURE,
      stockId,
      error: parsedError,
    });
  }
}

export function* workerLinkIncidentSaga(
  action: StockInspectLinkIncidentRequestAction
) {
  const { stockId, incidentId, positiveEventId } = action;

  try {
    yield call(linkIncident, stockId, incidentId, positiveEventId);
    yield put({ type: STOCK_INSPECT_LINK_INCIDENT_SUCCESS });
    yield put(requestStockInspect(stockId));
  } catch (error) {
    const parsedError = error as AxiosError;
    const responseData = parsedError.response?.data;

    yield put({
      type: STOCK_INSPECT_LINK_INCIDENT_FAILURE,
      error: responseData ?? parsedError.message ?? error,
    });
  }
}

export function* stockInspectSagaWatcher() {
  yield all([
    takeLatest(STOCK_INSPECT_REQUEST, workerStockInspectSaga),
    takeLatest(STOCK_INSPECT_LINK_INCIDENT_REQUEST, workerLinkIncidentSaga),
  ]);
}

export const requestStockInspect = (stockId: string) => ({
  type: STOCK_INSPECT_REQUEST,
  stockId,
});

export const requestStockInspectLinkIncident = (
  stockId: string,
  incidentId: number,
  positiveEventId: number
) => ({
  type: STOCK_INSPECT_LINK_INCIDENT_REQUEST,
  stockId,
  incidentId,
  positiveEventId,
});

export const resetStockInspectLinkIncident = () => ({
  type: STOCK_INSPECT_LINK_INCIDENT_RESET,
});

export const getIsStockInspectFetching = (state: RootState): boolean =>
  state.stockInspect.fetching;

export const getStockInspectError = (
  state: RootState
): AxiosError | Error | null => state.stockInspect.error;

export const getStockInspectData = (
  state: RootState,
  stockId: string
): { newStore: StockInspectResponse | null } => ({
  newStore: state.stockInspect.data.newStoreById[stockId] ?? null,
});

export const getIsStockInspectLinkIncidentSubmitting = (
  state: RootState
): boolean => state.stockInspect.linkIncident.submitting;

export const getStockInspectLinkIncidentError = (
  state: RootState
): unknown | null => state.stockInspect.linkIncident.error;

export const getDidStockInspectLinkIncidentSucceed = (
  state: RootState
): boolean => state.stockInspect.linkIncident.success;
